import React, { useState, useRef, useEffect } from "react";
import { Container, Row, Col, Form, Button, Table, Modal, Spinner, Alert } from "react-bootstrap";
import { FaTrash, FaPlus } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import "./AddPurchase.css";

const AddPurchase = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    supplier_id: "",
    date: new Date().toISOString().split('T')[0],
    bill_no: "",
    payment_method: "cash",
    payment_reference: "",
    gst_percentage: 18
  });

  const [purchaseItems, setPurchaseItems] = useState([]);
  const [errors, setErrors] = useState({});
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [currentProduct, setCurrentProduct] = useState({
    product_id: "",
    product_name: "",
    purchase_rate: "",
    quantity: ""
  });

  // Supplier modal states
  const [newSupplier, setNewSupplier] = useState({
    name: "",
    mobile: "",
    address: "",
    city: "",
    state: "",
    pin_code: "",
    gst_no: ""
  });
  const [supplierErrors, setSupplierErrors] = useState({});
  const [isAddingSupplier, setIsAddingSupplier] = useState(false);
  const [supplierSuccess, setSupplierSuccess] = useState("");

  // Refs for input fields
  const supplierRef = useRef(null);
  const dateRef = useRef(null);
  const billNoRef = useRef(null);
  const productSearchRef = useRef(null);
  const purchaseRateRef = useRef(null);
  const qtyRef = useRef(null);
  const paymentMethodRef = useRef(null);
  const paymentReferenceRef = useRef(null);

  // Fetch suppliers and products from API
  useEffect(() => {
    fetchDropdownData();
  }, []);

  const fetchDropdownData = async () => {
    try {
      // Fetch suppliers
      const suppliersResponse = await api.post("/supplier_list.php", {});
      if (suppliersResponse.data.head.code === 200) {
        setSuppliers(suppliersResponse.data.body);
      }

      // Fetch products
      const productsResponse = await api.post("/product_list.php", {});
      if (productsResponse.data.head.code === 200) {
        setProducts(productsResponse.data.body);
      }
    } catch (err) {
      console.error("Error fetching dropdown data:", err);
      // Fallback to sample data if API fails
      setSuppliers([
        { id: 1, name: "SSS Enterprises" },
        { id: 2, name: "ABC Suppliers" },
        { id: 3, name: "XYZ Traders" },
        { id: 4, name: "Global Imports" }
      ]);
      setProducts([
        { id: 1, name: "Pin Box - (00:4)" },
        { id: 2, name: "3 Inches Gum Tape - Red" },
        { id: 3, name: "3 Inches Gum Tape - Orange" },
        { id: 4, name: "Pin Box - (00:5)" }
      ]);
    }
  };

  // Filter products based on search input
  const getFilteredProducts = () => {
    if (!currentProduct.product_name) {
      return products.slice(0, 10);
    }
    
    const searchTerm = currentProduct.product_name.toLowerCase();
    return products.filter(product => 
      product.name.toLowerCase().includes(searchTerm)
    ).slice(0, 10);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleProductSearchChange = (e) => {
    const value = e.target.value;
    setCurrentProduct(prev => ({ 
      ...prev, 
      product_name: value,
      product_id: ""
    }));
    setShowProductDropdown(true);
  };

  // Handle product selection from dropdown
  const handleProductSelect = (product) => {
    setCurrentProduct({
      product_id: product.id,
      product_name: product.name,
      purchase_rate: "",
      quantity: ""
    });
    setShowProductDropdown(false);
    purchaseRateRef.current.focus();
  };

  const handleCurrentProductChange = (e) => {
    const { name, value } = e.target;
    setCurrentProduct(prev => ({ ...prev, [name]: value }));
  };

  const handleAutoAddItem = () => {
    if (!currentProduct.product_id || !currentProduct.purchase_rate || !currentProduct.quantity) {
      alert("Please fill all product details before adding");
      return;
    }

    const newItem = {
      id: Date.now(),
      product_id: currentProduct.product_id,
      product_name: currentProduct.product_name,
      purchase_rate: parseFloat(currentProduct.purchase_rate) || 0,
      quantity: parseInt(currentProduct.quantity) || 1,
      total: (parseFloat(currentProduct.purchase_rate) || 0) * (parseInt(currentProduct.quantity) || 1)
    };

    setPurchaseItems([...purchaseItems, newItem]);
    
    // Reset current product
    setCurrentProduct({
      product_id: "",
      product_name: "",
      purchase_rate: "",
      quantity: ""
    });
    
    // Focus back on product search for next entry
    productSearchRef.current.focus();
    setShowProductDropdown(false);
  };

  // Enhanced navigation with Enter key
  const handleKeyDown = (e, nextFieldRef, action = null) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      if (action) {
        action();
      } else if (nextFieldRef && nextFieldRef.current) {
        nextFieldRef.current.focus();
      }
    }
  };

  // Handle item field changes
  const handleItemChange = (index, field, value) => {
    const updatedItems = [...purchaseItems];
    
    if (field === 'purchase_rate') {
      updatedItems[index].purchase_rate = parseFloat(value) || 0;
    } else if (field === 'quantity') {
      updatedItems[index].quantity = parseInt(value) || 0;
    } else {
      updatedItems[index][field] = value;
    }
    
    // Calculate total if purchase rate or quantity changes
    if (field === 'purchase_rate' || field === 'quantity') {
      updatedItems[index].total = updatedItems[index].purchase_rate * updatedItems[index].quantity;
    }
    
    setPurchaseItems(updatedItems);
  };

  const handleDeleteItem = (index) => {
    const updatedItems = purchaseItems.filter((_, i) => i !== index);
    setPurchaseItems(updatedItems);
  };

  // Calculate totals
  const calculateSubTotal = () => {
    return purchaseItems.reduce((total, item) => total + (item.total || 0), 0);
  };

  const calculateGST = () => {
    const subTotal = calculateSubTotal();
    return (subTotal * (parseFloat(formData.gst_percentage) / 100));
  };

  const calculateGrandTotal = () => {
    const subTotal = calculateSubTotal();
    const gst = calculateGST();
    return subTotal + gst;
  };

  // Supplier Modal Functions
  const handleShowSupplierModal = () => {
    setShowSupplierModal(true);
    setSupplierErrors({});
    setSupplierSuccess("");
  };

  const handleCloseSupplierModal = () => {
    setShowSupplierModal(false);
    // Reset form
    setNewSupplier({
      name: "",
      mobile: "",
      address: "",
      city: "",
      state: "",
      pin_code: "",
      gst_no: ""
    });
    setSupplierErrors({});
    setSupplierSuccess("");
    setIsAddingSupplier(false);
  };

  // Handle new supplier form change
  const handleNewSupplierChange = (e) => {
    const { name, value } = e.target;
    setNewSupplier(prev => ({ ...prev, [name]: value }));
    // Clear error for this field when user starts typing
    if (supplierErrors[name]) {
      setSupplierErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  // Validate supplier form
  const validateSupplier = () => {
    const errors = {};
    
    if (!newSupplier.name.trim()) {
      errors.name = "Supplier name is required";
    }
    
    if (!newSupplier.mobile.trim()) {
      errors.mobile = "Mobile number is required";
    } else if (!/^\d{10}$/.test(newSupplier.mobile)) {
      errors.mobile = "Enter a valid 10-digit mobile number";
    }
    
    return errors;
  };

  // Handle adding supplier via API
  const handleAddSupplier = async () => {
    const validationErrors = validateSupplier();
    
    if (Object.keys(validationErrors).length > 0) {
      setSupplierErrors(validationErrors);
      return;
    }

    try {
      setIsAddingSupplier(true);
      setSupplierSuccess("");
      setSupplierErrors({});

      // Prepare payload matching supplier_add.php API
      const payload = {
        name: newSupplier.name,
        mobile: newSupplier.mobile,
        gst_no: newSupplier.gst_no || "",
        city: newSupplier.city || "",
        state: newSupplier.state || "",
        address: newSupplier.address || "",
        pin_code: newSupplier.pin_code || ""
      };

      // Call the supplier_add.php API
      const response = await api.post("/supplier_add.php", payload);

      if (response.data.head.code === 200) {
        // Success - add new supplier to local state
        const newSupplierObj = {
          id: response.data.body.supplier_id,
          name: newSupplier.name,
          mobile: newSupplier.mobile,
          gst_no: newSupplier.gst_no,
          city: newSupplier.city,
          state: newSupplier.state,
          address: newSupplier.address,
          pin_code: newSupplier.pin_code
        };

        // Add to suppliers list
        setSuppliers(prev => [...prev, newSupplierObj]);
        
        // Auto-select the new supplier
        setFormData(prev => ({ 
          ...prev, 
          supplier_id: response.data.body.supplier_id 
        }));

        // Show success message
        setSupplierSuccess(`Supplier "${newSupplier.name}" added successfully!`);
        
        // Reset form and close modal after 2 seconds
        setTimeout(() => {
          handleCloseSupplierModal();
          // Focus back on supplier dropdown
          if (supplierRef.current) {
            supplierRef.current.focus();
          }
        }, 2000);

      } else {
        // Handle API error
        const errorMsg = response.data.head.msg || "Failed to add supplier";
        setSupplierErrors({ api: errorMsg });
      }
    } catch (err) {
      console.error("Error adding supplier:", err);
      
      let errorMessage = "Failed to add supplier. Please try again.";
      
      if (err.response) {
        // The request was made and the server responded with a status code
        console.error("Response error:", err.response.data);
        errorMessage = err.response.data?.head?.msg || errorMessage;
      } else if (err.request) {
        // The request was made but no response was received
        console.error("No response received:", err.request);
        errorMessage = "No response from server. Please check your connection.";
      }
      
      setSupplierErrors({ api: errorMessage });
    } finally {
      setIsAddingSupplier(false);
    }
  };

  // Handle keydown in supplier modal
  const handleSupplierKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddSupplier();
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.supplier_id) newErrors.supplier_id = "Supplier is required";
    if (!formData.date) newErrors.date = "Date is required";
    if (purchaseItems.length === 0) newErrors.items = "At least one item is required";
    if (!formData.payment_method) newErrors.payment_method = "Payment method is required";
    
    return newErrors;
  };

  const formatDateToDMY = (date) => {
  if (!date) return "";
  const [yyyy, mm, dd] = date.split("-");
  return `${dd}-${mm}-${yyyy}`;
};


  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    try {
      setLoading(true);
      setSubmitError("");
      
      // Prepare payload for new structure (matching your updated backend)
      const payload = {
        supplier_id: formData.supplier_id,
        // date: formData.date,
        date: formatDateToDMY(formData.date),
        bill_no: formData.bill_no,
        payment_method: formData.payment_method,
        payment_reference: formData.payment_reference,
        sub_total: calculateSubTotal(),
        gst_percentage: formData.gst_percentage,
        gst_amount: calculateGST(),
        grand_total: calculateGrandTotal(),
        items: purchaseItems.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          purchase_rate: item.purchase_rate,
          quantity: item.quantity,
          total: item.total
        }))
      };

      
      const response = await api.post("/purchase_add.php", payload);
      
      if (response.data.head.code === 200) {
        alert(`Purchase added successfully!`);
        navigate("/purchaselist");
      } else {
        const errorMsg = response.data.head.msg || "Failed to add purchase";
        console.error("Backend error:", errorMsg);
        setSubmitError(errorMsg);
      }
    } catch (err) {
      console.error("Error adding purchase:", err);
      console.error("Error details:", err.response?.data || err.message);
      setSubmitError("Failed to add purchase. Please check console for details.");
    } finally {
      setLoading(false);
    }
  };

  const renderError = (field) =>
    errors[field] ? <div className="text-danger small mt-1">{errors[field]}</div> : null;

  // Render supplier field error
  const renderSupplierError = (field) =>
    supplierErrors[field] ? (
      <div className="text-danger small mt-1">{supplierErrors[field]}</div>
    ) : null;

  return (
    <Container className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Add Purchase</h4>
        <Button className="btn-top" onClick={() => navigate("/purchaselist")}>Back</Button>
      </div>

      {submitError && (
        <Alert variant="danger" className="mb-3">
          {submitError}
        </Alert>
      )}

      <Form onSubmit={handleSubmit} noValidate>
        <Row className="g-3">
          

          {/* Bill No Field */}
          <Col lg={3} md={3} sm={12}>
            <Form.Group>
              <Form.Label>Bill No</Form.Label>
              <Form.Control
                ref={billNoRef}
                name="bill_no"
                type="text"
                value={formData.bill_no}
                onChange={handleFormChange}
                onKeyDown={(e) => handleKeyDown(e, productSearchRef)}
                placeholder="Enter bill number"
              />
            </Form.Group>
          </Col>

          {/* Date Field */}
          <Col lg={3} md={3} sm={12}>
            <Form.Group>
              <Form.Label>Date *</Form.Label>
              <Form.Control
                ref={dateRef}
                name="date"
                type="date"
                value={formData.date}
                onChange={handleFormChange}
                onKeyDown={(e) => handleKeyDown(e, billNoRef)}
                isInvalid={!!errors.date}
              />
              {renderError("date")}
            </Form.Group>
          </Col>

          {/* Supplier Field */}
          <Col lg={6} md={6} sm={12}>
            <Form.Group>
              <Form.Label>Select Supplier *</Form.Label>
              <div className="d-flex gap-2">
                <Form.Select
                  ref={supplierRef}
                  name="supplier_id"
                  value={formData.supplier_id}
                  onChange={handleFormChange}
                  onKeyDown={(e) => handleKeyDown(e, dateRef)}
                  isInvalid={!!errors.supplier_id}
                >
                  <option value="">Select Supplier</option>
                  {suppliers.map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </option>
                  ))}
                </Form.Select>
                <Button 
                  variant="outline-primary" 
                  onClick={handleShowSupplierModal}
                  className="d-flex align-items-center"
                  title="Add New Supplier"
                  type="button"
                >
                  <FaPlus />
                </Button>
              </div>
              {renderError("supplier_id")}
            </Form.Group>
          </Col>

          {/* Payment Method Field 
          <Col lg={4} md={4} sm={6}>
            <Form.Group>
              <Form.Label>Payment Method *</Form.Label>
              <Form.Select
                ref={paymentMethodRef}
                name="payment_method"
                value={formData.payment_method}
                onChange={handleFormChange}
                onKeyDown={(e) => handleKeyDown(e, paymentReferenceRef)}
                isInvalid={!!errors.payment_method}
              >
                <option value="cash">Cash</option>
                <option value="card">Card</option>
                <option value="upi">UPI</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cheque">Cheque</option>
                <option value="credit">Credit</option>
              </Form.Select>
              {renderError("payment_method")}
            </Form.Group>
          </Col>*/}

          

          
          {/* Payment Reference Field 
          <Col lg={4} md={4} sm={12}>
            <Form.Group>
              <Form.Label>Payment Reference (Optional)</Form.Label>
              <Form.Control
                ref={paymentReferenceRef}
                name="payment_reference"
                type="text"
                value={formData.payment_reference}
                onChange={handleFormChange}
                onKeyDown={(e) => handleKeyDown(e, null)}
                placeholder="Transaction ID, Cheque No, etc."
              />
            </Form.Group>
          </Col>*/}

          {/* GST Percentage Field */}
          

          {/* Product Search Field */}
          <Col lg={6} md={6} sm={12}>
            <Form.Group className="position-relative">
              <Form.Label>Search Product *</Form.Label>
              <Form.Control
                ref={productSearchRef}
                name="product_name"
                type="text"
                value={currentProduct.product_name}
                onChange={handleProductSearchChange}
                onFocus={() => setShowProductDropdown(true)}
                onBlur={() => setTimeout(() => setShowProductDropdown(false), 200)}
                onKeyDown={(e) => handleKeyDown(e, purchaseRateRef)}
                placeholder="Type product name..."
                autoComplete="off"
              />
              {showProductDropdown && (
                <div className="custom-dropdown">
                  {getFilteredProducts().map((product, index) => (
                    <div
                      key={index}
                      className="dropdown-item"
                      onClick={() => handleProductSelect(product)}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <div className="fw-bold">{product.name} - {product.product_code}</div>
                    </div>
                  ))}
                  {getFilteredProducts().length === 0 && (
                    <div className="dropdown-item text-muted">
                      No products found
                    </div>
                  )}
                </div>
              )}
            </Form.Group>
          </Col>

          <Col lg={2} md={2} sm={6}>
            <Form.Group>
              <Form.Label>GST (%)</Form.Label>
              <Form.Control
                name="gst_percentage"
                type="number"
                value={formData.gst_percentage}
                onChange={handleFormChange}
                min="0"
                max="100"
                step="0.01"
              />
            </Form.Group>
          </Col>

          {/* Purchase Rate Field */}
          <Col lg={2} md={2} sm={6}>
            <Form.Group>
              <Form.Label>Rate (₹) *</Form.Label>
              <Form.Control
                ref={purchaseRateRef}
                name="purchase_rate"
                type="number"
                value={currentProduct.purchase_rate}
                onChange={handleCurrentProductChange}
                onKeyDown={(e) => handleKeyDown(e, qtyRef)}
                placeholder="0"
                min="0"
                step="0.01"
              />
            </Form.Group>
          </Col>

          {/* Quantity Field */}
          <Col lg={2} md={2} sm={6}>
            <Form.Group>
              <Form.Label>Quantity *</Form.Label>
              <Form.Control
                ref={qtyRef}
                name="quantity"
                type="number"
                value={currentProduct.quantity}
                onChange={handleCurrentProductChange}
                onKeyDown={(e) => handleKeyDown(e, null, handleAutoAddItem)}
                placeholder="0"
                min="1"
                step="0.01"
              />
              <small className="text-muted">Press Enter to add item</small>
            </Form.Group>
          </Col>
        </Row>

        {/* Purchase Items Table */}
        <Row className="mt-4">
          <Col xs={12}>
            <div className="table-responsive">
              <Table bordered className="purchase-table">
                <thead className="table-dark">
                  <tr>
                    <th width="5%" className="text-center">S.No</th>
                    <th width="30%">Product Name</th>
                    <th width="15%">Rate (₹)</th>
                    <th width="10%">Quantity</th>
                    <th width="15%" className="text-end">Total (₹)</th>
                    <th width="10%" className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {purchaseItems.map((item, index) => (
                    <tr key={item.id}>
                      <td className="text-center">{index + 1}</td>
                      <td>{item.product_name}</td>
                      <td>
                        <Form.Control
                          type="number"
                          value={item.purchase_rate}
                          onChange={(e) => handleItemChange(index, 'purchase_rate', e.target.value)}
                          placeholder="0"
                          min="0"
                          step="0.01"
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          placeholder="0"
                          min="1"
                        />
                      </td>
                      <td className="text-end">₹{item.total.toFixed(2)}</td>
                      <td className="text-center">
                        <FaTrash
                          className="delete-icon"
                          style={{ cursor: 'pointer', color: '#dc3545' }}
                          onClick={() => handleDeleteItem(index)}
                          title="Delete Item"
                        />
                      </td>
                    </tr>
                  ))}
                  {purchaseItems.length === 0 && (
                    <tr>
                      <td colSpan="6" className="text-center text-muted py-3">
                        No items added yet. Search products and press Enter in quantity field to add items.
                      </td>
                    </tr>
                  )}
                </tbody>
                {purchaseItems.length > 0 && (
                  <tfoot>
                    <tr>
                      <td colSpan="3" className="text-end fw-bold">Subtotal:</td>
                      <td colSpan="3" className="text-end fw-bold">
                        ₹{calculateSubTotal().toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="3" className="text-end">
                        GST ({formData.gst_percentage}%):
                      </td>
                      <td colSpan="3" className="text-end">
                        ₹{calculateGST().toFixed(2)}
                      </td>
                    </tr>
                    <tr className="table-active">
                      <td colSpan="3" className="text-end fw-bold fs-5">Grand Total:</td>
                      <td colSpan="3" className="text-end fw-bold fs-5">
                        ₹{calculateGrandTotal().toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </Table>
            </div>
          </Col>
        </Row>

        {renderError("items")}

        {/* Submit Buttons */}
        <div className="d-flex justify-content-end mt-4 gap-2">
          <Button 
            type="submit" 
            className="btn-submit"
            disabled={loading || purchaseItems.length === 0}
          >
            {loading ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Submitting...
              </>
            ) : (
              "Submit Purchase"
            )}
          </Button>
          <Button 
            type="button" 
            className="btn-top" 
            onClick={() => navigate("/purchaselist")}
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </Form>

      {/* Add Supplier Modal */}
      <Modal show={showSupplierModal} onHide={handleCloseSupplierModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add New Supplier</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {supplierSuccess && (
            <Alert variant="success" className="mb-3">
              {supplierSuccess}
            </Alert>
          )}

          {supplierErrors.api && (
            <Alert variant="danger" className="mb-3">
              {supplierErrors.api}
            </Alert>
          )}

          <Form onKeyDown={handleSupplierKeyDown}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Supplier Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={newSupplier.name}
                    onChange={handleNewSupplierChange}
                    placeholder="Enter supplier name"
                    required
                    isInvalid={!!supplierErrors.name}
                    autoFocus
                  />
                  {renderSupplierError("name")}
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Mobile No *</Form.Label>
                  <Form.Control
                    type="text"
                    name="mobile"
                    value={newSupplier.mobile}
                    onChange={handleNewSupplierChange}
                    placeholder="Enter 10-digit mobile number"
                    maxLength="10"
                    required
                    isInvalid={!!supplierErrors.mobile}
                  />
                  {renderSupplierError("mobile")}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>GST Number</Form.Label>
                  <Form.Control
                    type="text"
                    name="gst_no"
                    value={newSupplier.gst_no}
                    onChange={handleNewSupplierChange}
                    placeholder="Enter GST number"
                  />
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    name="city"
                    value={newSupplier.city}
                    onChange={handleNewSupplierChange}
                    placeholder="Enter city"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>State</Form.Label>
                  <Form.Control
                    type="text"
                    name="state"
                    value={newSupplier.state}
                    onChange={handleNewSupplierChange}
                    placeholder="Enter state"
                  />
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>PIN Code</Form.Label>
                  <Form.Control
                    type="text"
                    name="pin_code"
                    value={newSupplier.pin_code}
                    onChange={handleNewSupplierChange}
                    placeholder="Enter PIN code"
                    maxLength="6"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="address"
                value={newSupplier.address}
                onChange={handleNewSupplierChange}
                placeholder="Enter full address"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={handleCloseSupplierModal}
            disabled={isAddingSupplier}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAddSupplier} 
            disabled={!newSupplier.name.trim() || !newSupplier.mobile.trim() || isAddingSupplier}
          >
            {isAddingSupplier ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Adding...
              </>
            ) : (
              "Add Supplier"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AddPurchase;